import { GameType } from 'names/do-names';
import Components from 'slotworks/components/components';
import { FadeIn, FadeOut } from 'slotworks/graphics/animation/base-animations';
import Layers from 'slotworks/graphics/layers/layers';
import gameState from 'slotworks/model/game-state';
import soundManager from 'slotworks/sound/sound-manager';
import Contract from 'slotworks/utils/contract';
import Point from 'slotworks/utils/geom/point';
import { parallel, sequence } from 'slotworks/utils/race-utils';
import Timer from 'slotworks/utils/timer';
import * as TweenJS from 'slotworks/utils/tween';
import GameInit from 'states/game-init';
import RevolverWinTransitions from './revolver-win-transitions';

export default class BigWinTransitions extends RevolverWinTransitions {
    public EXPLOSION_TIME: number = 400;
    public TEXT_ZOOM = 1.1;
    public TEXT_ZOOM_TWEEN = 0.95;
    public big: PIXI.Sprite;
    private done: boolean;

    public in(): Contract<void> {
        return new Contract(resolve => {
            this.resetEverything();
            this.done = false;
            Layers.CelebrationForeground.getButton('continue').setVisible(false);
            Layers.CelebrationForeground.getText('continue').visible = false;
            this.big = Layers.CelebrationContent.getSprite('big');

            this.big.visible = false;
            RevolverWinTransitions.winValue.visible = false;
            RevolverWinTransitions.winText.visible = false;

            this.inResolve = resolve;
            RevolverWinTransitions.background.visible = true;
            FadeIn(RevolverWinTransitions.background, this.BOUNCE_TIME).execute();
            RevolverWinTransitions.currentTiers = this.getSelectedTiers().length;
            let wintype = ['big', 'mega', 'supermega', 'epic'];

            RevolverWinTransitions.currentTiers = this.getSelectedTiers().length;
            this.zoomInStacks(450, 1.8, 300).execute();
            this.timers.push(
                Timer.setTimeout(() => {
                    this.big.visible = true;
                    RevolverWinTransitions.winText.visible = true;
                    RevolverWinTransitions.winValue.visible = true;
                    soundManager.customEvent(`${wintype[RevolverWinTransitions.currentTiers - 1]}_win`);
                    RevolverWinTransitions.winValue.scale.set(1, 1);
                    parallel([() => this.zoomElement(RevolverWinTransitions.winText), () => this.zoomTextIn(1.1, 1, 500), () => this.titleIn(this.big, new Point(0.5, 1))]).then(
                        () => {
                            RevolverWinTransitions.coins.emit = true;
                            RevolverWinTransitions.coins.frequency = 0.1;
                            if (!this.done) {
                                this.hover(RevolverWinTransitions.winValue, 1.05, 800, false);
                                if (RevolverWinTransitions.currentTiers > 1) {
                                    this.timers.push(
                                        Timer.setTimeout(() => {
                                            if (this.big) {
                                                TweenJS.removeAll(this.big);
                                                this.titleOut(this.big, new Point(0.5, 1), 400).then(() => (this.big.visible = false));
                                            }
                                        }, 2500)
                                    );
                                } else {
                                    Components.bigWin.totalReached.addOnce(this.onAmountReached);
                                }
                            }
                            this.resolveIn();
                        }
                    );
                }, 200)
            );
        });
    }

    public skipIn() {
        soundManager.customEvent("start_background_ambience");
        Components.bigWin.totalReached.removeAll();
        if (this.done) {
            this.resolveIn();
            return;
        }
        this.destroy();
        soundManager.customEvent('big_win');
        this.big.visible = true;
        RevolverWinTransitions.winText.visible = true;
        RevolverWinTransitions.winValue.visible = true;

        this.big.scale.set(1, 1);
        if (this.big.anchor.x == 0) {
            this.big.anchor.set(0.5, 1);
            this.big.x += 0.5 * this.big.width;
            this.big.y += this.big.height;
        }
        RevolverWinTransitions.winText.scale.set(1, 1);
        if (RevolverWinTransitions.winText.anchor.x == 0) {
            RevolverWinTransitions.winText.anchor.set(0.5, 0);
            RevolverWinTransitions.winText.x += 0.5 * RevolverWinTransitions.winText.width;
        }
        RevolverWinTransitions.glowContainer.scale.set(1.5, 1.5);
        this.resolveIn();
    }

    public out(): Contract<void> {
        this.done = true;
        return sequence([
            () =>
                Contract.wrap(() => {
                    this.clearTimers();
                    TweenJS.removeAll(this.big.scale);
                    TweenJS.removeAll(RevolverWinTransitions.winText.scale);
                    TweenJS.removeAll(RevolverWinTransitions.glowContainer.scale);
                    TweenJS.removeAll(RevolverWinTransitions.winValue.scale);
                    const totalWin = gameState.getCurrentGame().totalCreditsWon;
                    Components.betBar.winnings.setWinnings(totalWin, totalWin, 750);
                    RevolverWinTransitions.coins.emit = false;
                    soundManager.customEvent('Plaque_Exit');
                }),
            () =>
                parallel([
                    () =>
                        sequence([
                            () =>
                                parallel([
                                    () => this.zoomOut(RevolverWinTransitions.winText, 300),
                                    () => this.zoomOut(RevolverWinTransitions.winValue, 300),
                                    () => this.zoomOut(this.big, 300)
                                ]),
                            () =>
                                Contract.wrap(() => {
                                    this.big.visible = false;
                                    RevolverWinTransitions.winText.visible = false;
                                    RevolverWinTransitions.winValue.visible = false;
                                })
                        ]),
                    () => this.zoomOutStacks()
                ]),
            () =>
                Contract.wrap(() => {
                    soundManager.customEvent('celebration_end');
                }),
            () => FadeOut(RevolverWinTransitions.background, this.BOUNCE_TIME),
            () => Contract.getTimeoutContract(750),
            () =>
                Contract.wrap(() => {
                    Layers.SmartButton.show();
                    Layers.BetBar.show();
                    soundManager.customEvent("start_background_ambience");
                })
        ]);
    }

    public destroy() {
        this.clearTimers();

        super.destroy();
    }

    private onAmountReached = () => {
        if (this.done) return;
        RevolverWinTransitions.coins.frequency = 0.03;
        soundManager.customEvent('Win_Coin_Fountain');
        this.emphasize(RevolverWinTransitions.winText, 1.25, 100, 100).then(() => {
            if (!this.done) this.hover(RevolverWinTransitions.winText, 1.05, 750, false);
        });
        this.emphasize(RevolverWinTransitions.winValue, 1.1, 150, 100).then(() => {
            if (!this.done) this.hover(RevolverWinTransitions.winValue, 1.05, 800, false);
        });
        this.emphasize(this.big, 1.25, 0, 100).then(() => {
            if (!this.done) this.hover(this.big, 1.05, 800, false);
        });
    };
}



// WEBPACK FOOTER //
// ./src/transitions/big-win-transition.ts